import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SEO } from "@/components/SEO";

export default function Subscribe() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem("ittv_sub_email");
    if (saved) {
      setEmail(saved);
      api.get(`/subscribe/check`, { params: { email: saved } })
        .then((r) => setStatus(r.data))
        .catch(() => {});
    }
  }, []);

  const startCheckout = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email first.");
    setBusy(true);
    try {
      localStorage.setItem("ittv_sub_email", email.toLowerCase());
      const { data } = await api.post("/subscribe/checkout", {
        email,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const alreadyActive = status?.active;

  return (
    <>
      <SEO
        title="Julia Fosah's Weekly Long Read · Subscribe · ITTV News"
        description="Support independent journalism. $5/month gets you Julia Fosah's Weekly Long Read plus the full subscriber archive on ITTV News."
      />

      <section className="bg-[color:var(--text)] text-[color:var(--bg)] py-20" data-testid="subscribe-hero">
        <div className="container-editorial grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="kicker text-[color:var(--accent)] mb-3">Subscribers Only</div>
            <h1 className="font-serif text-5xl md:text-7xl font-bold leading-none tracking-tight">
              Julia Fosah&apos;s Weekly Long Read
            </h1>
            <p className="mt-6 text-xl opacity-80 max-w-lg leading-relaxed">
              One serious, sourced, subscriber-only essay every Sunday. No churn. No sponsored posts. Just Julia&apos;s best writing.
            </p>
            <div className="mt-8 flex items-baseline gap-3">
              <div className="font-serif text-7xl font-bold">$5</div>
              <div className="opacity-70">/ month · cancel anytime</div>
            </div>
          </div>

          <div className="bg-[color:var(--bg)] text-[color:var(--text)] p-8 md:p-10 border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
            <div className="kicker text-[color:var(--accent)] mb-2">What you get</div>
            <ul className="space-y-3 mb-8 text-lg">
              {[
                "The Weekly Long Read — full-length subscribers-only essay",
                "Complete access to the premium archive",
                "Weekly Sunday dispatch, ad-free",
                "Direct reply email to Julia every month",
                "Support independent journalism",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Check className="text-[color:var(--accent)] shrink-0 mt-1" size={18} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            {alreadyActive ? (
              <div className="border p-4 flex items-start gap-3" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="subscribe-active">
                <Check className="text-[color:var(--accent)] shrink-0" />
                <div>
                  <div className="font-serif text-xl font-bold">You&apos;re subscribed.</div>
                  <div className="text-sm text-[color:var(--text-muted)]">Active until {status.subscription?.expires_at ? new Date(status.subscription.expires_at).toLocaleDateString() : "—"}.</div>
                  <button onClick={() => nav("/")} className="btn-outline mt-4">Read the archive</button>
                </div>
              </div>
            ) : (
              <form onSubmit={startCheckout} className="space-y-4" data-testid="subscribe-form">
                <div>
                  <label className="kicker block mb-2">Your email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-transparent border px-4 py-3 outline-none"
                    style={{ borderColor: "hsl(var(--border-hsl))" }}
                    data-testid="subscribe-email"
                  />
                </div>
                <button disabled={busy} className="btn-accent w-full py-4 flex items-center justify-center gap-2" data-testid="subscribe-checkout">
                  <Lock size={16} /> {busy ? "Redirecting to Stripe…" : "Subscribe for $5/month"}
                </button>
                <p className="text-xs text-[color:var(--text-muted)]">Secure checkout by Stripe. Cancel anytime from your Stripe portal.</p>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="container-editorial py-20">
        <div className="max-w-3xl mx-auto">
          <div className="kicker text-[color:var(--accent)] mb-3">A note from the editor</div>
          <h2 className="font-serif text-4xl font-bold tracking-tight">Why this exists</h2>
          <div className="mt-6 space-y-5 text-lg leading-relaxed text-[color:var(--text-muted)]">
            <p>Serious journalism costs time. Not just typing time — reading time, source time, court-record time. Advertising can&apos;t pay for that anymore. Readers can.</p>
            <p>The Weekly Long Read is one 3,000+ word essay every Sunday, on politics, media, or culture, by Julia Fosah. It is what she considers her most careful work, and it exists only because subscribers make it exist.</p>
            <p>Five dollars a month. That&apos;s the price of a coffee. And it keeps a newsroom independent.</p>
          </div>
        </div>
      </section>
    </>
  );
}
